import React from 'react';
import { useFilter, type Key } from '@heroui/react';
import { PRODUCT_FILTER_DESCRIPTORS } from '../productFilterDescriptors';
import {
    FILTERS_COPY,
    NO_BRAND_KEY,
    fromBrandPickerKeys,
    matchesBrandSearch,
    toBrandPickerKeys,
    type BrandFilterValue,
    type FilterWords,
    type NamedOption,
} from './productFiltersMeta';
import type { FilterOptionsState } from './useFilterOptionsState';

export type BrandFilterPickerProps = {
    options: FilterOptionsState<NamedOption>;
    value: BrandFilterValue;
    onChange: (value: BrandFilterValue) => void;
};

export type UseBrandFilterPickerResult = {
    pickedKeys: string[];
    tags: { key: string; label: FilterWords }[];
    filter: ReturnType<typeof matchesBrandSearch>;
    onSelectionChange: (keys: unknown) => void;
    onRemove: (keys: Set<Key>) => void;
    onClear: () => void;
};

/** Normalizes the selection and describes tags without translating in the hook. */
export const useBrandFilterPicker = (
    items: readonly NamedOption[],
    value: BrandFilterValue,
    onChange: (value: BrandFilterValue) => void,
): UseBrandFilterPickerResult => {
    const { contains } = useFilter({ sensitivity: 'base' });
    const filter = React.useMemo(
        () => matchesBrandSearch(contains),
        [contains],
    );
    const nameById = React.useMemo(
        () => new Map(items.map((option) => [option.id, option.name])),
        [items],
    );
    const pickedKeys = toBrandPickerKeys(value);
    const tags = pickedKeys.map((key) => {
        const name = nameById.get(key);
        const label: FilterWords =
            key === NO_BRAND_KEY
                ? { key: `${FILTERS_COPY}.noBrand` }
                : name === undefined
                  ? { key: `${FILTERS_COPY}.unknownValue` }
                  : { text: name };
        return { key, label };
    });

    return {
        pickedKeys,
        tags,
        filter,
        onSelectionChange: (keys) =>
            onChange(fromBrandPickerKeys(Array.isArray(keys) ? keys : [])),
        onRemove: (keys) =>
            onChange(
                fromBrandPickerKeys(pickedKeys.filter((key) => !keys.has(key))),
            ),
        onClear: () => onChange(PRODUCT_FILTER_DESCRIPTORS.brand.cleared),
    };
};
